import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, subDays, startOfDay } from 'date-fns';
import moment from 'moment';

export type IncidentStatusFilter = 'ALL' | 'Open' | 'Closed';

export interface IIncidentCustomFilter {
    startDate: Date | null;
    endDate: Date | null;
    airline: string;
    airport: string;
    issueReason: string;
    status: IncidentStatusFilter;
}

interface IncidentState {
    search: string;
    appliedSearch: string;
    hasfilterApplied: boolean;
    hasSearchFiltersApplied: boolean;
    customFilter: IIncidentCustomFilter;
    appliedFilter: IIncidentCustomFilter;
}

const getDefaultStartDate = () => subDays(startOfDay(new Date()), 7);
const getDefaultEndDate = () => addDays(startOfDay(new Date()), 6);

const initialState: IncidentState = {
    search: '',
    appliedSearch: '',
    hasfilterApplied: false,
    hasSearchFiltersApplied: false,
    customFilter: {
        startDate: getDefaultStartDate(),
        endDate: getDefaultEndDate(),
        airline: 'ALL',
        airport: 'ALL',
        issueReason: 'ALL',
        status: 'ALL',
    },
    appliedFilter: {
        startDate: getDefaultStartDate(),
        endDate: getDefaultEndDate(),
        airline: 'ALL',
        airport: 'ALL',
        issueReason: 'ALL',
        status: 'ALL',
    },
};

const incidentSlice = createSlice({
    name: 'incidents',
    initialState,
    reducers: {
        setSearchFilter: (state, action: PayloadAction<string>) => {
            state.search = action.payload;
            state.hasfilterApplied = true;
        },
        setStartDate: (state, action: PayloadAction<Date | null>) => {
            state.customFilter.startDate = action.payload;
            state.customFilter.endDate = moment(action.payload).add(13, 'days').toDate();
            state.hasfilterApplied = true;
        },
        setEndDate: (state, action: PayloadAction<Date | null>) => {
            state.customFilter.endDate = action.payload;
            state.hasfilterApplied = true;
        },
        setAirlineFilter: (state, action: PayloadAction<string>) => {
            state.customFilter.airline = action.payload;
            state.hasfilterApplied = true;
        },
        setAirportFilter: (state, action: PayloadAction<string>) => {
            state.customFilter.airport = action.payload;
            state.hasfilterApplied = true;
        },
        setIssueReasonFilter: (state, action: PayloadAction<string>) => {
            state.customFilter.issueReason = action.payload;
            state.hasfilterApplied = true;
        },
        setStatusFilter: (state, action: PayloadAction<IncidentStatusFilter>) => {
            state.customFilter.status = action.payload;
            state.hasfilterApplied = true;
        },
        applySearch: (state) => {
            state.appliedSearch = state.search;
            state.appliedFilter = { ...state.customFilter };
            state.hasSearchFiltersApplied = true;
            state.hasfilterApplied =
                state.appliedSearch.trim().length > 0 ||
                state.appliedFilter.airline !== 'ALL' ||
                state.appliedFilter.airport !== 'ALL' ||
                state.appliedFilter.issueReason !== 'ALL' ||
                state.appliedFilter.status !== 'ALL' ||
                state.appliedFilter.startDate?.toDateString() !== getDefaultStartDate().toDateString() ||
                state.appliedFilter.endDate?.toDateString() !== getDefaultEndDate().toDateString();
        },
        clearAllFilters: (state) => {
            state.hasSearchFiltersApplied = false;
            state.hasfilterApplied = false;
            state.search = '';
            state.appliedSearch = '';
            state.customFilter.startDate = getDefaultStartDate();
            state.customFilter.endDate = getDefaultEndDate();
            state.customFilter.airline = 'ALL';
            state.customFilter.airport = 'ALL';
            state.customFilter.issueReason = 'ALL';
            state.customFilter.status = 'ALL';
            state.appliedFilter.startDate = getDefaultStartDate();
            state.appliedFilter.endDate = getDefaultEndDate();
            state.appliedFilter.airline = 'ALL';
            state.appliedFilter.airport = 'ALL';
            state.appliedFilter.issueReason = 'ALL';
            state.appliedFilter.status = 'ALL';
        },
    },
});

export const {
    setSearchFilter,
    setStartDate,
    setEndDate,
    setAirlineFilter,
    setAirportFilter,
    setIssueReasonFilter,
    setStatusFilter,
    applySearch,
    clearAllFilters,
} = incidentSlice.actions;

export default incidentSlice.reducer;
